import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  Payment,
  Assessment,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`finance-tabpanel-${index}`}
      aria-labelledby={`finance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Finance: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { invoices, addInvoice, updateInvoice, deleteInvoice } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [formData, setFormData] = useState({
    patient: "",
    amount: "",
    date: "",
    dueDate: "",
    paymentMethod: "",
    insuranceProvider: "",
    status: "Pending",
  });

  const validationRules = {
    patient: commonValidationRules.name,
    amount: commonValidationRules.amount,
    date: commonValidationRules.date,
    dueDate: commonValidationRules.date,
    paymentMethod: { required: true },
    insuranceProvider: { required: true },
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, invoice?: any) => {
    setDialogType(type);
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        patient: invoice.patient,
        amount: invoice.amount.toString(),
        date: invoice.date,
        dueDate: invoice.dueDate,
        paymentMethod: invoice.paymentMethod,
        insuranceProvider: invoice.insuranceProvider,
        status: invoice.status,
      });
    } else {
      setEditingInvoice(null);
      setFormData({
        patient: "",
        amount: "",
        date: "",
        dueDate: "",
        paymentMethod: "",
        insuranceProvider: "",
        status: "Pending",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingInvoice(null);
    setFormData({
      patient: "",
      amount: "",
      date: "",
      dueDate: "",
      paymentMethod: "",
      insuranceProvider: "",
      status: "Pending",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const invoiceData = {
        invoiceNumber: `INV-2024-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
        patient: formData.patient,
        amount: parseFloat(formData.amount),
        date: formData.date,
        dueDate: formData.dueDate,
        paymentMethod: formData.paymentMethod,
        insuranceProvider: formData.insuranceProvider,
        status: formData.status,
      };

      if (editingInvoice) {
        updateInvoice(editingInvoice.id, invoiceData);
        showSuccess("Invoice updated successfully");
      } else {
        addInvoice(invoiceData);
        showSuccess("Invoice created successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save invoice");
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        deleteInvoice(id);
        showSuccess("Invoice deleted successfully");
      } catch (error) {
        showError("Failed to delete invoice");
      }
    }
  };

  // Mock data
  const generalLedgerData = [
    {
      id: 1,
      account: "Cash - Main Account",
      debit: 50000,
      credit: 0,
      balance: 50000,
      status: "Active",
    },
    {
      id: 2,
      account: "Accounts Receivable",
      debit: 25000,
      credit: 0,
      balance: 25000,
      status: "Active",
    },
    {
      id: 3,
      account: "Accounts Payable",
      debit: 0,
      credit: 15000,
      balance: -15000,
      status: "Active",
    },
    {
      id: 4,
      account: "Revenue - Consultations",
      debit: 0,
      credit: 75000,
      balance: 75000,
      status: "Active",
    },
    {
      id: 5,
      account: "Expenses - Salaries",
      debit: 30000,
      credit: 0,
      balance: 30000,
      status: "Active",
    },
  ];

  const accountsPayableData = [
    {
      id: 1,
      vendor: "Medical Supplies Co.",
      amount: 5000,
      dueDate: "2024-01-15",
      status: "Pending",
    },
    {
      id: 2,
      vendor: "Equipment Maintenance Ltd.",
      amount: 3200,
      dueDate: "2024-01-20",
      status: "Pending",
    },
    {
      id: 3,
      vendor: "Pharmaceuticals Inc.",
      amount: 8500,
      dueDate: "2024-01-25",
      status: "Paid",
    },
    {
      id: 4,
      vendor: "Utilities Company",
      amount: 1200,
      dueDate: "2024-01-30",
      status: "Pending",
    },
  ];

  const accountsReceivableData = [
    {
      id: 1,
      customer: "Insurance Company A",
      amount: 15000,
      dueDate: "2024-01-10",
      status: "Pending",
    },
    {
      id: 2,
      customer: "Corporate Client B",
      amount: 8500,
      dueDate: "2024-01-15",
      status: "Pending",
    },
    {
      id: 3,
      customer: "Insurance Company C",
      amount: 22000,
      dueDate: "2024-01-20",
      status: "Paid",
    },
    {
      id: 4,
      customer: "Individual Patient",
      amount: 1200,
      dueDate: "2024-01-25",
      status: "Pending",
    },
  ];

  const budgetData = [
    {
      category: "Personnel",
      budgeted: 500000,
      actual: 485000,
      variance: -15000,
      percentage: 97,
    },
    {
      category: "Medical Supplies",
      budgeted: 150000,
      actual: 162000,
      variance: 12000,
      percentage: 108,
    },
    {
      category: "Equipment",
      budgeted: 200000,
      actual: 195000,
      variance: -5000,
      percentage: 97.5,
    },
    {
      category: "Utilities",
      budgeted: 80000,
      actual: 82000,
      variance: 2000,
      percentage: 102.5,
    },
    {
      category: "Marketing",
      budgeted: 50000,
      actual: 45000,
      variance: -5000,
      percentage: 90,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Paid":
      case "Active":
        return "success";
      case "Pending":
        return "warning";
      case "Overdue":
        return "error";
      default:
        return "default";
    }
  };

  const getVarianceColor = (variance: number) => {
    return variance >= 0 ? "success" : "error";
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("finance.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {t("finance.description")}
      </Typography>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountBalance color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 2,450,000</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Receipt color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 1,850,000</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accounts Receivable
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Payment color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 650,000</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accounts Payable
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Assessment color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 950,000</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Profit
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Invoices" />
            <Tab label={t("finance.generalLedger")} />
            <Tab label={t("finance.accountsPayable")} />
            <Tab label={t("finance.accountsReceivable")} />
            <Tab label={t("finance.budgeting")} />
            <Tab label={t("finance.financialReporting")} />
          </Tabs>
        </Box>

        {/* Invoices Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Invoice Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("invoice")}
            >
              Create Invoice
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Insurance Provider</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.patient}</TableCell>
                    <TableCell align="right">
                      SAR {invoice.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{invoice.date}</TableCell>
                    <TableCell>{invoice.dueDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{invoice.paymentMethod}</TableCell>
                    <TableCell>{invoice.insuranceProvider}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small"
                        onClick={() => handleOpenDialog("invoice", invoice)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDelete(invoice.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* General Ledger Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">General Ledger</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("account")}
            >
              Add Account
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generalLedgerData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.account}</TableCell>
                    <TableCell align="right">
                      {row.debit.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {row.credit.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {row.balance.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={getStatusColor(row.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Accounts Payable Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Accounts Payable</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("payable")}
            >
              Add Payable
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accountsPayableData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.vendor}</TableCell>
                    <TableCell align="right">
                      SAR {row.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={getStatusColor(row.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Payment />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Accounts Receivable Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Accounts Receivable</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("receivable")}
            >
              Add Receivable
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accountsReceivableData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.customer}</TableCell>
                    <TableCell align="right">
                      SAR {row.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={getStatusColor(row.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Receipt />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Budget Management Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Budget Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("budget")}
            >
              Add Budget Item
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Budgeted</TableCell>
                  <TableCell align="right">Actual</TableCell>
                  <TableCell align="right">Variance</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {budgetData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.category}</TableCell>
                    <TableCell align="right">
                      SAR {row.budgeted.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {row.actual.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {row.variance >= 0 ? (
                          <TrendingUp color="success" />
                        ) : (
                          <TrendingDown color="error" />
                        )}
                        <Typography
                          color={getVarianceColor(row.variance)}
                          sx={{ ml: 1 }}
                        >
                          SAR {Math.abs(row.variance).toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.percentage}%</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Financial Reports Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Financial Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Income Statement
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Comprehensive profit and loss statement
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Balance Sheet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Assets, liabilities, and equity overview
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cash Flow Statement
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Cash inflows and outflows analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "invoice" && (editingInvoice ? "Edit Invoice" : "Create New Invoice")}
          {dialogType === "account" && "Add New Account"}
          {dialogType === "payable" && "Add Accounts Payable"}
          {dialogType === "receivable" && "Add Accounts Receivable"}
          {dialogType === "budget" && "Add Budget Item"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "invoice" ? (
              <>
                <TextField
                  fullWidth
                  label="Patient Name"
                  variant="outlined"
                  value={formData.patient}
                  onChange={(e) => handleInputChange("patient", e.target.value)}
                  error={!!errors.patient}
                  helperText={errors.patient}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  variant="outlined"
                  value={formData.amount}
                  onChange={(e) => handleInputChange("amount", e.target.value)}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  variant="outlined"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  variant="outlined"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select 
                    label="Payment Method"
                    value={formData.paymentMethod}
                    onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Credit Card">Credit Card</MenuItem>
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Insurance Provider</InputLabel>
                  <Select 
                    label="Insurance Provider"
                    value={formData.insuranceProvider}
                    onChange={(e) => handleInputChange("insuranceProvider", e.target.value)}
                  >
                    <MenuItem value="Tawuniya">Tawuniya</MenuItem>
                    <MenuItem value="Bupa">Bupa</MenuItem>
                    <MenuItem value="MedGulf">MedGulf</MenuItem>
                    <MenuItem value="N/A">N/A</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select 
                    label="Status"
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status">
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={dialogType === "invoice" ? handleSubmit : handleCloseDialog}>
            {dialogType === "invoice" ? (editingInvoice ? "Update" : "Create") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Finance;
